import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useProjects } from '../../contexts/ProjectContext';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiPlus, FiEdit3, FiTrash2, FiUser, FiUsers, FiChevronDown, FiChevronRight, FiSave, FiCheck, FiX } = FiIcons;

const CharacterManager = ({ project }) => {
  const { updateProject } = useProjects();
  const [characters, setCharacters] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingCharacter, setEditingCharacter] = useState(null);
  const [activeCharacter, setActiveCharacter] = useState(null);
  const [expandedSections, setExpandedSections] = useState({});
  const [saveStatus, setSaveStatus] = useState(null);
  const [debounceTimeout, setDebounceTimeout] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);

  // Initialize characters from project content
  useEffect(() => {
    if (project && project.content) {
      console.log('Loading characters from project:', project.id);
      const projectCharacters = project.content.characters || [];
      setCharacters(projectCharacters);
      
      // Set first character as active if none selected
      if (projectCharacters.length > 0 && !activeCharacter) {
        setActiveCharacter(projectCharacters[0]);
      }
    }
  }, [project?.content?.characters]);

  // Character template
  const createNewCharacter = () => ({
    id: Date.now().toString(),
    name: '',
    role: 'supporting', // protagonist, antagonist, supporting
    age: '',
    occupation: '',
    description: '',
    appearance: '',
    personality: '',
    background: '',
    motivation: '',
    goals: '',
    fears: '',
    strengths: '',
    weaknesses: '',
    character_arc: '',
    relationships: '',
    spiritual_journey: '',
    dialogue_voice: '',
    mannerisms: '',
    secrets: '',
    created_at: new Date().toISOString()
  });

  // Save characters to project
  const saveCharacters = async (updatedCharacters) => {
    setSaveStatus('saving');
    setErrorMessage(null);
    
    // Clear any existing timeout
    if (debounceTimeout) {
      clearTimeout(debounceTimeout);
    }

    const timeoutId = setTimeout(async () => {
      try {
        console.log('Saving characters to project:', project.id);
        console.log('Characters data:', updatedCharacters);
        
        await updateProject(project.id, { characters: updatedCharacters });
        setSaveStatus('saved');
        setTimeout(() => setSaveStatus(null), 2000);
      } catch (error) {
        console.error('Error saving characters:', error);
        setSaveStatus('error');
        setErrorMessage('Failed to save characters. Please try again.');
        setTimeout(() => {
          setSaveStatus(null);
          setErrorMessage(null);
        }, 4000);
      }
    }, 500);

    setDebounceTimeout(timeoutId);
  };

  // Add new character
  const handleAddCharacter = () => {
    const newCharacter = createNewCharacter();
    setEditingCharacter(newCharacter);
    setShowAddModal(true);
  };

  // Save character (add or update)
  const handleSaveCharacter = (characterData) => {
    let updatedCharacters;
    if (editingCharacter && characters.find(c => c.id === editingCharacter.id)) {
      // Update existing character
      updatedCharacters = characters.map(c => 
        c.id === editingCharacter.id ? characterData : c
      );
    } else {
      // Add new character
      updatedCharacters = [...characters, characterData];
    }

    setCharacters(updatedCharacters);
    setActiveCharacter(characterData);
    saveCharacters(updatedCharacters);
    setShowAddModal(false);
    setEditingCharacter(null);
  };

  // Delete character
  const handleDeleteCharacter = (characterId) => {
    if (window.confirm('Are you sure you want to delete this character?')) {
      const updatedCharacters = characters.filter(c => c.id !== characterId);
      setCharacters(updatedCharacters);
      
      // If deleted character was active, select another
      if (activeCharacter && activeCharacter.id === characterId) {
        setActiveCharacter(updatedCharacters.length > 0 ? updatedCharacters[0] : null);
      }
      
      saveCharacters(updatedCharacters);
    }
  };

  // Edit character
  const handleEditCharacter = (character) => {
    setEditingCharacter(character);
    setShowAddModal(true);
  };

  // Toggle expanded sections
  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'protagonist': return 'bg-primary-100 text-primary-700';
      case 'antagonist': return 'bg-red-100 text-red-700';
      default: return 'bg-spiritual-100 text-spiritual-700';
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'protagonist': return FiUser;
      case 'antagonist': return FiIcons.FiShield;
      default: return FiUsers;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-secondary-800">Character Profiles</h2>
          <p className="text-secondary-600 mt-2">
            Create detailed profiles for all your story characters
          </p>
        </div>
        <div className="flex items-center space-x-4">
          {saveStatus && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className={`flex items-center space-x-2 px-3 py-1 rounded-lg ${
                saveStatus === 'saving' 
                  ? 'bg-primary-50 text-primary-600' 
                  : saveStatus === 'error'
                    ? 'bg-red-50 text-red-600'
                    : 'bg-green-50 text-green-600'
              }`}
            >
              {saveStatus === 'saving' ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600"></div>
                  <span className="text-sm">Saving...</span>
                </>
              ) : saveStatus === 'error' ? (
                <>
                  <SafeIcon icon={FiIcons.FiAlertCircle} className="w-4 h-4" />
                  <span className="text-sm">Error saving</span>
                </>
              ) : (
                <>
                  <SafeIcon icon={FiCheck} className="w-4 h-4" />
                  <span className="text-sm">Saved</span>
                </>
              )}
            </motion.div>
          )}
          
          {errorMessage && (
            <div className="text-red-600 text-sm">{errorMessage}</div>
          )}
          
          <button
            onClick={handleAddCharacter}
            className="flex items-center space-x-2 bg-gradient-to-r from-primary-600 to-spiritual-600 text-white px-4 py-2 rounded-lg hover:from-primary-700 hover:to-spiritual-700 transition-colors"
          >
            <SafeIcon icon={FiPlus} className="w-5 h-5" />
            <span>Add Character</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Character List Sidebar */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-xl shadow-lg p-4">
            <h3 className="font-semibold text-secondary-800 mb-4">Characters ({characters.length})</h3>
            
            {characters.length > 0 ? (
              <div className="space-y-2">
                {characters.map((character) => (
                  <div
                    key={character.id}
                    className={`p-3 rounded-lg border cursor-pointer transition-all ${
                      activeCharacter && activeCharacter.id === character.id
                        ? 'border-primary-500 bg-primary-50'
                        : 'border-secondary-200 hover:border-primary-300 hover:bg-primary-25'
                    }`}
                    onClick={() => setActiveCharacter(character)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <SafeIcon
                          icon={getRoleIcon(character.role)}
                          className="w-4 h-4 text-secondary-600"
                        />
                        <div>
                          <p className="font-medium text-secondary-800">
                            {character.name || 'Unnamed Character'}
                          </p>
                          <span
                            className={`text-xs px-2 py-0.5 rounded-full ${getRoleColor(
                              character.role
                            )}`}
                          >
                            {character.role}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center space-x-1">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditCharacter(character);
                          }}
                          className="p-1 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded"
                        >
                          <SafeIcon icon={FiEdit3} className="w-4 h-4" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteCharacter(character.id);
                          }}
                          className="p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded"
                        >
                          <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <SafeIcon icon={FiUsers} className="w-12 h-12 text-secondary-300 mx-auto mb-4" />
                <p className="text-secondary-600 text-sm mb-4">No characters yet</p>
                <button
                  onClick={handleAddCharacter}
                  className="text-primary-600 hover:text-primary-700 font-medium text-sm"
                >
                  Create your first character
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Character Detail Panel */}
        <div className="lg:col-span-3">
          {activeCharacter ? (
            <CharacterDetailPanel
              character={activeCharacter}
              expandedSections={expandedSections}
              toggleSection={toggleSection}
              onEdit={() => handleEditCharacter(activeCharacter)}
            />
          ) : (
            <div className="bg-white rounded-xl shadow-lg p-12 text-center">
              <SafeIcon icon={FiUser} className="w-16 h-16 text-secondary-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-secondary-800 mb-2">No Character Selected</h3>
              <p className="text-secondary-600 mb-6">
                Select a character from the list or create a new one to view their profile.
              </p>
              <button
                onClick={handleAddCharacter}
                className="inline-flex items-center space-x-2 bg-gradient-to-r from-primary-600 to-spiritual-600 text-white px-6 py-3 rounded-lg font-semibold hover:from-primary-700 hover:to-spiritual-700 transition-all duration-300"
              >
                <SafeIcon icon={FiPlus} className="w-5 h-5" />
                <span>Add Character</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Character Modal */}
      <CharacterModal
        isOpen={showAddModal}
        character={editingCharacter}
        onSave={handleSaveCharacter}
        onClose={() => {
          setShowAddModal(false);
          setEditingCharacter(null);
        }}
      />
    </div>
  );
};

// Character Detail Panel Component
const CharacterDetailPanel = ({ character, expandedSections, toggleSection, onEdit }) => {
  const sections = [
    {
      id: 'basic',
      title: 'Basic Information',
      fields: [
        { label: 'Name', value: character.name },
        { label: 'Role', value: character.role },
        { label: 'Age', value: character.age },
        { label: 'Occupation', value: character.occupation }
      ]
    },
    {
      id: 'physical',
      title: 'Physical Description',
      fields: [
        { label: 'Appearance', value: character.appearance, multiline: true }
      ]
    },
    {
      id: 'personality',
      title: 'Personality & Psychology',
      fields: [
        { label: 'Personality', value: character.personality, multiline: true },
        { label: 'Strengths', value: character.strengths, multiline: true },
        { label: 'Weaknesses', value: character.weaknesses, multiline: true },
        { label: 'Fears', value: character.fears, multiline: true }
      ]
    },
    {
      id: 'background',
      title: 'Background & History',
      fields: [
        { label: 'Background', value: character.background, multiline: true },
        { label: 'Secrets', value: character.secrets, multiline: true }
      ]
    },
    {
      id: 'motivation',
      title: 'Goals & Motivation',
      fields: [
        { label: 'Motivation', value: character.motivation, multiline: true },
        { label: 'Goals', value: character.goals, multiline: true },
        { label: 'Character Arc', value: character.character_arc, multiline: true }
      ]
    },
    {
      id: 'relationships',
      title: 'Relationships & Voice',
      fields: [
        { label: 'Relationships', value: character.relationships, multiline: true },
        { label: 'Dialogue Voice', value: character.dialogue_voice, multiline: true },
        { label: 'Mannerisms', value: character.mannerisms, multiline: true }
      ]
    },
    {
      id: 'spiritual',
      title: 'Spiritual Journey',
      fields: [
        { label: 'Spiritual Journey', value: character.spiritual_journey, multiline: true }
      ]
    }
  ];

  return (
    <div className="bg-white rounded-xl shadow-lg">
      {/* Header */}
      <div className="p-6 border-b border-secondary-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <SafeIcon
              icon={
                character.role === 'protagonist'
                  ? FiUser
                  : character.role === 'antagonist'
                  ? FiIcons.FiShield
                  : FiUsers
              }
              className="w-8 h-8 text-primary-600"
            />
            <div>
              <h3 className="text-2xl font-bold text-secondary-800">
                {character.name || 'Unnamed Character'}
              </h3>
              <span
                className={`text-sm px-3 py-1 rounded-full ${
                  character.role === 'protagonist'
                    ? 'bg-primary-100 text-primary-700'
                    : character.role === 'antagonist'
                    ? 'bg-red-100 text-red-700'
                    : 'bg-spiritual-100 text-spiritual-700'
                }`}
              >
                {character.role.charAt(0).toUpperCase() + character.role.slice(1)}
              </span>
            </div>
          </div>
          <button
            onClick={onEdit}
            className="flex items-center space-x-2 bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors"
          >
            <SafeIcon icon={FiEdit3} className="w-4 h-4" />
            <span>Edit</span>
          </button>
        </div>
        {character.description && (
          <p className="text-secondary-600 mt-3">{character.description}</p>
        )}
      </div>

      {/* Sections */}
      <div className="p-6 space-y-4">
        {sections.map((section) => (
          <div key={section.id} className="border border-secondary-200 rounded-lg overflow-hidden">
            <div
              className="flex items-center justify-between bg-gradient-to-r from-primary-50 to-spiritual-50 p-4 cursor-pointer"
              onClick={() => toggleSection(section.id)}
            >
              <h4 className="font-semibold text-secondary-800">{section.title}</h4>
              <SafeIcon
                icon={expandedSections[section.id] ? FiChevronDown : FiChevronRight}
                className="w-5 h-5 text-secondary-600"
              />
            </div>
            <AnimatePresence>
              {expandedSections[section.id] && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="overflow-hidden"
                >
                  <div className="p-4 space-y-4">
                    {section.fields.map((field, index) => (
                      <div key={index}>
                        <label className="block text-sm font-medium text-secondary-700 mb-1">
                          {field.label}
                        </label>
                        {field.value ? (
                          <div className={`text-secondary-800 ${field.multiline ? 'whitespace-pre-wrap' : ''}`}>
                            {field.value}
                          </div>
                        ) : (
                          <div className="text-secondary-400 italic">Not specified</div>
                        )}
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        ))}
      </div>
    </div>
  );
};

// Character Modal Component
const CharacterModal = ({ isOpen, character, onSave, onClose }) => {
  const [formData, setFormData] = useState(character || {});
  const [activeTab, setActiveTab] = useState('basic');

  useEffect(() => {
    if (character) {
      setFormData(character);
    }
  }, [character]);

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  const tabs = [
    { id: 'basic', label: 'Basic' },
    { id: 'physical', label: 'Physical' },
    { id: 'personality', label: 'Personality' },
    { id: 'background', label: 'Background' },
    { id: 'goals', label: 'Goals' },
    { id: 'relationships', label: 'Relationships' },
    { id: 'spiritual', label: 'Spiritual' }
  ];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-secondary-200">
          <h3 className="text-xl font-bold text-secondary-800">
            {character && formData.name ? `Edit ${formData.name}` : 'Add New Character'}
          </h3>
          <button
            onClick={onClose}
            className="text-secondary-500 hover:text-secondary-700 transition-colors"
          >
            <SafeIcon icon={FiX} className="w-6 h-6" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-secondary-200 overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-3 font-medium whitespace-nowrap ${
                activeTab === tab.id
                  ? 'text-primary-600 border-b-2 border-primary-600'
                  : 'text-secondary-600 hover:text-primary-500'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Form Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          <form onSubmit={handleSubmit}>
            {activeTab === 'basic' && (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-1">
                      Character Name *
                    </label>
                    <input
                      type="text"
                      value={formData.name || ''}
                      onChange={(e) => handleChange('name', e.target.value)}
                      className="w-full px-4 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
                      placeholder="Enter character name"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-1">
                      Role *
                    </label>
                    <select
                      value={formData.role || 'supporting'}
                      onChange={(e) => handleChange('role', e.target.value)}
                      className="w-full px-4 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
                    >
                      <option value="protagonist">Protagonist</option>
                      <option value="antagonist">Antagonist</option>
                      <option value="supporting">Supporting Character</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-1">
                      Age
                    </label>
                    <input
                      type="text"
                      value={formData.age || ''}
                      onChange={(e) => handleChange('age', e.target.value)}
                      className="w-full px-4 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
                      placeholder="e.g., 25, Early 30s, Teenager"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-1">
                      Occupation
                    </label>
                    <input
                      type="text"
                      value={formData.occupation || ''}
                      onChange={(e) => handleChange('occupation', e.target.value)}
                      className="w-full px-4 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
                      placeholder="What do they do for work?"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-1">
                    Brief Description
                  </label>
                  <textarea
                    value={formData.description || ''}
                    onChange={(e) => handleChange('description', e.target.value)}
                    rows={3}
                    className="w-full px-4 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
                    placeholder="A brief summary of this character..."
                  />
                </div>
              </div>
            )}

            {activeTab === 'physical' && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-1">
                    Physical Appearance
                  </label>
                  <textarea
                    value={formData.appearance || ''}
                    onChange={(e) => handleChange('appearance', e.target.value)}
                    rows={6}
                    className="w-full px-4 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
                    placeholder="Describe their physical appearance, height, build, hair, eyes, distinctive features, clothing style, etc."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-1">
                    Mannerisms & Habits
                  </label>
                  <textarea
                    value={formData.mannerisms || ''}
                    onChange={(e) => handleChange('mannerisms', e.target.value)}
                    rows={4}
                    className="w-full px-4 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
                    placeholder="How do they move, speak, or behave? Any unique habits or quirks?"
                  />
                </div>
              </div>
            )}

            {activeTab === 'personality' && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-1">
                    Personality Overview
                  </label>
                  <textarea
                    value={formData.personality || ''}
                    onChange={(e) => handleChange('personality', e.target.value)}
                    rows={4}
                    className="w-full px-4 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
                    placeholder="Describe their personality, temperament, and general demeanor..."
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-1">
                      Strengths & Virtues
                    </label>
                    <textarea
                      value={formData.strengths || ''}
                      onChange={(e) => handleChange('strengths', e.target.value)}
                      rows={4}
                      className="w-full px-4 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
                      placeholder="What are their positive qualities and strengths?"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-1">
                      Weaknesses & Flaws
                    </label>
                    <textarea
                      value={formData.weaknesses || ''}
                      onChange={(e) => handleChange('weaknesses', e.target.value)}
                      rows={4}
                      className="w-full px-4 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
                      placeholder="What are their character flaws and weaknesses?"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-1">
                    Fears & Insecurities
                  </label>
                  <textarea
                    value={formData.fears || ''}
                    onChange={(e) => handleChange('fears', e.target.value)}
                    rows={3}
                    className="w-full px-4 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
                    placeholder="What do they fear? What makes them insecure or anxious?"
                  />
                </div>
              </div>
            )}

            {activeTab === 'background' && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-1">
                    Background & History
                  </label>
                  <textarea
                    value={formData.background || ''}
                    onChange={(e) => handleChange('background', e.target.value)}
                    rows={6}
                    className="w-full px-4 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
                    placeholder="Describe their personal history, family background, important life events, education, etc."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-1">
                    Secrets & Hidden Elements
                  </label>
                  <textarea
                    value={formData.secrets || ''}
                    onChange={(e) => handleChange('secrets', e.target.value)}
                    rows={4}
                    className="w-full px-4 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
                    placeholder="What secrets do they keep? What don't other characters know about them?"
                  />
                </div>
              </div>
            )}

            {activeTab === 'goals' && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-1">
                    Core Motivation
                  </label>
                  <textarea
                    value={formData.motivation || ''}
                    onChange={(e) => handleChange('motivation', e.target.value)}
                    rows={4}
                    className="w-full px-4 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
                    placeholder="What drives this character? What is their deepest motivation?"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-1">
                    Goals & Desires
                  </label>
                  <textarea
                    value={formData.goals || ''}
                    onChange={(e) => handleChange('goals', e.target.value)}
                    rows={4}
                    className="w-full px-4 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
                    placeholder="What do they want to achieve? What are their short-term and long-term goals?"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-1">
                    Character Arc & Growth
                  </label>
                  <textarea
                    value={formData.character_arc || ''}
                    onChange={(e) => handleChange('character_arc', e.target.value)}
                    rows={4}
                    className="w-full px-4 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
                    placeholder="How will this character change and grow throughout the story?"
                  />
                </div>
              </div>
            )}

            {activeTab === 'relationships' && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-1">
                    Key Relationships
                  </label>
                  <textarea
                    value={formData.relationships || ''}
                    onChange={(e) => handleChange('relationships', e.target.value)}
                    rows={5}
                    className="w-full px-4 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
                    placeholder="Describe their relationships with other characters, family, friends, enemies, etc."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-1">
                    Dialogue Voice & Speech Patterns
                  </label>
                  <textarea
                    value={formData.dialogue_voice || ''}
                    onChange={(e) => handleChange('dialogue_voice', e.target.value)}
                    rows={4}
                    className="w-full px-4 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
                    placeholder="How do they speak? What's unique about their dialogue style, vocabulary, accent, etc.?"
                  />
                </div>
              </div>
            )}

            {activeTab === 'spiritual' && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-1">
                    Spiritual Journey & Faith
                  </label>
                  <textarea
                    value={formData.spiritual_journey || ''}
                    onChange={(e) => handleChange('spiritual_journey', e.target.value)}
                    rows={6}
                    className="w-full px-4 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
                    placeholder="Describe their relationship with faith, spiritual beliefs, journey with God, struggles, growth, etc."
                  />
                </div>
              </div>
            )}
          </form>
        </div>

        {/* Footer */}
        <div className="flex justify-end space-x-3 p-6 border-t border-secondary-200">
          <button
            onClick={onClose}
            className="px-4 py-2 text-secondary-600 hover:text-secondary-800 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="px-6 py-2 bg-gradient-to-r from-primary-600 to-spiritual-600 text-white rounded-lg hover:from-primary-700 hover:to-spiritual-700 transition-colors flex items-center space-x-2"
          >
            <SafeIcon icon={FiSave} className="w-4 h-4" />
            <span>Save Character</span>
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default CharacterManager;